import React, { useState, useContext, useEffect } from "react"
import { graphql } from "gatsby"
import {
  Editor,
  EditorState,
  CompositeDecorator,
  convertFromRaw,
  DefaultDraftBlockRenderMap,
} from "draft-js"
// importing draft.css in Layout.js
import styled, { ThemeContext } from "styled-components"
import css from "../../lib/styled-system/css"
import Image from "../utils/Image"
import { Map } from "immutable"

import Box from "../atoms/Box"
import { Paragraph, Title, Text } from "../atoms/Typography"
import Link from "../utils/Link"
import { compose, space } from "styled-system"

const Wrapper = styled.div`
  ${css({
    color: "text",
  })}
  .RichEditor-blockquote {
    ${props =>
      css({
        backgroundColor: props.bg,
      })}
    font-size: 14px;
    padding: 36px 40px;
    margin: 44px 24px;
    .public-DraftStyleDefault-block {
      margin-top: 0px;
    }
    @media (max-width: 640px) {
      margin-left: 12px;
      margin-right: 12px;
      padding-left: 24px;
      padding-right: 24px;
    }
  }
  a {
    span {
      font-weight: inherit;
    }
  }
  figure {
    margin: 0;
  }
  .RichEditor-code {
    font-family: "Courier", "Courier New", monospace;
    ${props =>
      css({
        backgroundColor: props.bg,
      })}
    font-size: 14px;
    padding: 36px 40px;
    margin: 44px 24px;
    .public-DraftStyleDefault-block {
      margin-top: 0px;
    }
    @media (max-width: 640px) {
      margin-left: 12px;
      margin-right: 12px;
      padding-left: 24px;
      padding-right: 24px;
    }
  }
  .RichEditor-unordered-list-item,
  .RichEditor-ordered-list-item {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
  .RichEditor-unstyled {
    margin-bottom: 16px;
  }
  ${compose(space)}
`

//Link styling, mapping block styles to typography variant
export const getLinkVariantFromBlockType = blockType => {
  switch (blockType) {
    case "header-one":
      return "h1"
    case "header-two":
      return "h2"
    case "header-three":
      return "h3"
    case "paragraph":
    case "unstyled":
      return "p"
    default:
      return null
  }
}

const RichText = props => {
  const {
    content,
    images,
    section,
    isArticle,
    textAlign,
    fontSize,
    variant,
    ...rest
  } = props

  const [editorState, setEditorState] = useState(null)
  const contentParsed = content?.internal?.content
    ? JSON.parse(content.internal.content)
    : content

  // ======================
  // BELOW IS HOW we configure our color object
  // for inline color application
  // ======================
  const themeForText = useContext(ThemeContext)
  const createColorMap = theme => {
    const colorObj = {}
    const lights = theme?.colors?.brand?.lights
    const mediums = theme?.colors?.brand?.mediums
    const darks = theme?.colors?.brand?.darks
    lights.map((color, idx) => {
      colorObj[`light${idx}`] = { color: `${color}` }
    })
    mediums.map((color, idx) => {
      colorObj[`medium${idx}`] = { color: `${color}` }
    })
    darks.map((color, idx) => {
      colorObj[`dark${idx}`] = { color: `${color}` }
    })
    return colorObj
  }
  const createCustomFontMap = theme => {
    const fontObj = {}
    const typographyObj = theme.typography

    fontObj.customFont1 = typographyObj.customFont1
    fontObj.customFont2 = typographyObj.customFont2
    fontObj.customFont3 = typographyObj.customFont3

    return fontObj
  }
  const styleMap = {
    ...createColorMap(themeForText),
    ...createCustomFontMap(themeForText),
  }

  // ======================
  // BEGIN DECORATORS (image and links)
  // colors will probably be implemented using a decorator
  // ======================
  const ImageHandler = ({ contentState, entityKey }) => {
    const { id } = contentState.getEntity(entityKey).getData()
    const arrayOfSingleImage = images.filter(
      image =>
        image?.strapi_id?.toString() === id.toString() ||
        image?.id?.toString() === id.toString()
    )

    if (arrayOfSingleImage.length > 0) {
      const image = arrayOfSingleImage[0]
      return (
        <Box width="100%" my="44px">
          <Image alternativeText="" {...image} />
        </Box>
      )
    }
    return <div>Image has been removed</div>
  }

  function findImageEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity()
      if (entityKey === null) {
        return false
      }
      const entityType = contentState.getEntity(entityKey).getType()
      return entityType === "IMAGE"
    }, callback)
  }

  const LinkHandler = props => {
    const customColor = getColorData(props)
    const theme = useContext(ThemeContext)
    const linkStylesPath = theme?.colors?.linkStyles
    const linkData = props.contentState.getEntity(props.entityKey).getData()
    const blockType = props.contentState.getBlockForKey(props.blockKey).type
    const linkVariant = getLinkVariantFromBlockType(blockType)
    let fontStylesObj = {}
    if (blockType.includes("customFont")) {
      fontStylesObj = createFontStylesObj(blockType)
    }
    return (
      //Link requires https:// or http:// included in data, should we provide some kind of validation?
      <Link aria-label="Link" {...linkData}>
        <Text
          style={{ transition: "opacity 0.1s ease" }}
          fontWeight={linkVariant === "h1" ? "inherit" : null}
          variant={getLinkVariantFromBlockType(blockType)}
          color={customColor ? customColor.color : "linkColor"}
          hover={customColor ? { opacity: 0.7 } : { color: "linkHover" }}
          active={
            customColor
              ? { color: customColor.color }
              : { color: "linkClicked" }
          }
          opacity={1}
          {...fontStylesObj}
        >
          {props.children}
        </Text>
      </Link>
    )
  }

  function findLinkEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity()
      if (entityKey === null) {
        return false
      }
      const entityType = contentState.getEntity(entityKey).getType()
      return entityType === "LINK"
    }, callback)
  }

  const colorHandler = props => {
    const styleObj = getColorData(props)
    return <span style={styleObj}>{props.children}</span>
  }

  function findColorEntities(contentBlock, callback, contentState) {
    contentBlock.findStyleRanges(character => {
      let customColor =
        character._map._root?.entries?.[0]?.[1]?._map?._map?._root?.entries

      let match = false
      for (let color in styleMap) {
        if (character._map._root?.entries?.[0]?.[1].has(color)) match = true
      }
      if (customColor) {
        for (let innerArr of customColor) {
          if (innerArr[0].slice(0, 8) === "color: #") match = true
        }
      }
      return match
    }, callback)
  }

  const customFontHandler = props => {
    const { contentState, blockKey } = props
    const blockType = contentState.getBlockForKey(blockKey).getType()
    const fontStylesObj = createFontStylesObj(blockType)
    if (Object.keys(fontStylesObj).length) {
      return (
        <Paragraph {...fontStylesObj}>
          {props.children.map(child => {
            const customColor = getColorData(child.props)
            return <span style={customColor}>{child}</span>
          })}
        </Paragraph>
      )
    }
    return <></>
  }

  const createFontStylesObj = blockType => {
    const { customFont1, customFont2, customFont3 } = styleMap
    const customFonts = { customFont1, customFont2, customFont3 }

    let fontStyles = {}
    if (customFonts?.[blockType]) {
      const { fontSize, mobileFontSize } = customFonts[blockType]
      const desktopFontSize = themeForText.fontSizes[`${fontSize}`]
      const fontSizeObj = {
        _: `${mobileFontSize}px`,
        md: `${desktopFontSize}px`,
      }

      fontStyles = { ...customFonts[blockType], fontSize: fontSizeObj }
    }
    return fontStyles
  }

  function findCustomFontStyles(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const blockType = contentBlock.getType()
      
      return blockType.includes("customFont")
    }, callback)
  }
  // helper function to find color in style array
  function findColorCode(colorData) {
    if (!colorData) return
    for (let val of colorData) {
      if (
        val[0].slice(0, -1) === "light" ||
        val[0].slice(0, -1) === "medium" ||
        val[0].slice(0, -1) === "dark"
      ) {
        return val[0]
      }
    }
  }
  // helper function to find a color entity inside richtext
  // used for both richtext Links and Color handlers
  function getColorData(props) {
    const colorData = props?.children
      ? props.children[0]?.props?.styleSet?._map?._list?._tail?.array
      : props?.styleSet
      ? props.styleSet?._map?._list?._tail?.array
      : undefined

    if (colorData === undefined) return null

    let styleObj = {}
    const customColor =
      colorData[colorData.length - 1][0].slice(0, 8) === "color: #"
        ? colorData[colorData.length - 1][0]
        : null
    if (!customColor) {
      const accessCode = findColorCode(colorData)
      styleObj = styleMap[accessCode]
    } else {
      const hexCode = customColor.split(": ")[1]
      styleObj = { color: hexCode }
    }
    return styleObj
  }

  // combine the decorators
  const decorator = new CompositeDecorator([
    {
      strategy: findImageEntities,
      component: ImageHandler,
    },
    {
      strategy: findLinkEntities,
      component: LinkHandler,
    },
    {
      strategy: findCustomFontStyles,
      component: customFontHandler,
    },
    {
      strategy: findColorEntities,
      component: colorHandler,
    },
  ])

  // ======================
  // END DECORATORS
  // ======================

  // we need this useEffect to update the preview in Morphic
  useEffect(() => {
    setEditorState(
      contentParsed
        ? EditorState.createWithContent(
            convertFromRaw(contentParsed),
            decorator
          )
        : EditorState.createEmpty(decorator)
    )
  }, [content, images])

  // get blockquote/code background color (first bg color that doesn't match bg of richtext)
  // get current section theme
  let currentTheme = useContext(ThemeContext)
  let theme = "light"
  let bg = 0
  if (section) {
    theme = section.theme
    bg = section.bg
    if (theme === "default") theme = "light"
  }
  let blockQuoteBg = 0
  let themeBgColors = currentTheme?.themes[theme]?.colors?.background
  if (themeBgColors) {
    for (let i = 0; i < themeBgColors.length; i++) {
      if (themeBgColors[i] !== themeBgColors[bg]) {
        blockQuoteBg = i
        break
      }
    }
  }

  // BLOCK STYLING
  // Two options for adding styles to blocks:

  // 1. we use block style function when it's not necessary to
  // wrap with custom component
  function getBlockStyle(block) {
    switch (block.getType()) {
      case "blockquote":
        return "RichEditor-blockquote"
      case "code-block":
        return "RichEditor-code"
      case "unordered-list-item":
        return "RichEditor-unordered-list-item"
      case "ordered-list-item":
        return "RichEditor-ordered-list-item"
      case "paragraph":
      case "unstyled":
        return "RichEditor-unstyled"
      default:
        return null
    }
  }

  // 2. use block render map when you need to wrap with custom
  // component (usually typography)
  let baseBlockRenderMap = Map({
    "header-one": {
      element: "div",
      wrapper: <Title variant="h1" mb={3} />,
    },
    "header-two": {
      element: "div",
      wrapper: <Title variant="h2" mb={3} />,
    },
    "header-three": {
      element: "div",
      wrapper: <Title variant="h3" mb={3} />,
    },
    subtitle: {
      element: "div",
      wrapper: <Paragraph fontSize="smaller" variant="label" mb={4} />,
    },
    detailText: {
      element: "div",
      wrapper: <Paragraph variant="detailText" mb={4} />,
    },
    MegaMenuDescription: {
      element: "div",
      wrapper: <Paragraph variant="MegaMenuDescription" mb={4} />,
    },
    customFont1: {
      element: "div",
      wrapper: <Paragraph variant="customFont1" mb={4} />,
    },
    customFont2: {
      element: "div",
      wrapper: <Paragraph variant="customFont2" mb={4} />,
    },
    customFont3: {
      element: "div",
      wrapper: <Paragraph variant="customFont3" mb={4} />,
    },
  })

  // If the variant is "MegaMenuDescription", update the "unstyled" and "paragraph"
  // blocks to rendered with <Paragraph variant="MegaMenuDescription" />.
  if (variant === "MegaMenuDescription") {
    const megaMenuWrapper = baseBlockRenderMap.get("MegaMenuDescription")
    baseBlockRenderMap = baseBlockRenderMap.set("unstyled", megaMenuWrapper)
    baseBlockRenderMap = baseBlockRenderMap.set("paragraph", megaMenuWrapper)
  }

  // this applies the normal rendering process (including any decorators)
  // within custom blockRenderMap (necessary to support things like links and inline-styles
  // in headings and subtitles)
  const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(
    baseBlockRenderMap
  )

  if (!editorState?.getCurrentContent()?.hasText()) return null

  return (
    <Box width={"100%"} {...rest}>
      <Wrapper textAlign={textAlign} bg={"background." + blockQuoteBg} px="1px">
        <Paragraph fontSize={fontSize} as={isArticle ? "article" : "div"}>
          <Editor
            textAlignment={textAlign}
            blockRenderMap={extendedBlockRenderMap}
            blockStyleFn={getBlockStyle}
            editorState={editorState}
            contentEditable={false}
            readOnly
          />
        </Paragraph>
      </Wrapper>
    </Box>
  )
}

export default React.memo(RichText)

export const query = graphql`
  fragment RichText on STRAPI__COMPONENT_ATOMS_RICH_TEXT {
    content {
      internal {
        content
      }
    }
    images {
      ...ImageWithConstrainedLayoutAnd703pxWidth
    }
  }
`
